/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import styled from 'styled-components';
import { ReactComponent as GlobeLogo } from '@bostonglobe/logos/logos/bglobe/bglobe-logo-black.svg';
import PortlandLogo from '~assets/images/pressherald.png';
import useBusiness from '~lib/useBusiness';
import Topper from '../components/base/Topper';
import SeoSocial from '../components/base/SeoSocial';
import Credits from '../components/base/Credits';
import VideoPlayerElement from '../components/body/VideoPlayerElement';
import { ReactComponent as Divider } from '~assets/images/lobster-divider.svg';

const SectionHed = styled.h1`
  margin-bottom: 30px;
  text-align: center;
  font-size: 1.5rem;
  @media screen and (min-width: 600px) {
    font-size: 2rem;
  }
`;
// logos of orgs for about
const Partnership = styled.div`
  max-width: 800px;
  margin: 30px auto;
  padding: 10px;
  .left-margin {
    margin-left: 5%;
  }
  .logos {
    width: 250px;
    display: flex;
    gap: 20px;
    margin: 10px auto;
    flex-direction: column;
    align-items: center;
    svg {
      width: 250px;
    }
    @media screen and (min-width: 600px) {
      float: right;
      margin: 30px 0 10px 20px
    }
  }
`;

const StoryLinks = styled.div`
  max-width: 1000px;
  margin: 60px auto;
  padding: 10px;
  text-align: center;
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      opacity: 0.85;
      .link-hed {
        text-decoration: underline;
      }
    }
  }
  .pub-date {
    font-size: 14px;
  }
  .link-hed {
    font-size: 2.265rem;
    line-height: normal;
    margin-bottom: 10px;
  }
  .link-dek {
    font-size: 1rem;
    line-height: 1.4rem;
    margin-bottom: 20px;
  }
  @media screen and (min-width: 600px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 65px;
  }
`;
const Related = styled.div`
  margin: 30px auto;
  max-width: 680px; 
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  gap: 40px;
  padding: 10px;
  a {
    display: flex;
    gap: 15px;
    text-decoration: none;
    color: inherit;
    &:hover {
      color:#767676;
    }
    figure {
      flex-basis: 50%;
      img {
        object-fit: conrtain;
      }
    }
    div {
      flex-basis: 50%;
      .pub-date {
        font-size: 14px;
        margin-bottom: 10px;
      }
      .related-hed {
        font-size: 16px;
        line-height: 24px;
      }
    }
    @media screen and (min-width: 600px) {
      figure {
        flex-basis: 60%;
      }
      div {
        flex-basis: 40%;
        .related-hed {
          font-size: 24px;
          line-height: 32px;
        }
      }
    }
  }
`;

const relatedCoverage = [
  {
    url: 'https://www.bostonglobe.com/2021/12/16/metro/lobstering-climate-change-maine-read-7-takeaways-our-lobster-trap-series/',
    pubDate: 'December 16, 2021',
    text: 'Lobstering, climate change, and Maine. Read 7 takeaways from our ‘Lobster Trap’ series',
    image: 'https://bostonglobe-prod.cdn.arcpublishing.com/resizer/RFthbsOkxVdswD8SaUjNJah36WQ=/1024x0/cloudfront-us-east-1.images.arcpublishing.com/bostonglobe/V5KLO6NXBXIQBJGKN4FYZQU7BE.JPG',
  },
  {
    url: 'https://www.bostonglobe.com/2021/12/12/metro/one-connecticuts-last-lobstermen-sticks-with-it-despite-his-near-empty-hauls/',
    pubDate: 'December 12, 2021',
    text: 'One of Connecticut’s last lobstermen sticks with it, despite his near empty hauls',
    image: 'https://cloudfront-us-east-1.images.arcpublishing.com/bostonglobe/66DB5AAQGWUJBD55QON74ZJU2M.JPG'
  },
  // {
  //   url: 'https://thelobstertrap.splashthat.com/',
  //   pubDate: 'GLOBE EVENT',
  //   text: 'Coming Wednesday: Sign up now for a virtual event with the reporters behind The Lobster Trap',
  //   image: 'https://d24wuq6o951i2g.cloudfront.net/img/events/id/458/458287529/assets/52f32ba17ebcfeda14046254472f2f02.3AK75JRXJ6R6JBOWZETABHV5ME.jpeg',
  // },
  {
    url: 'https://apps.bostonglobe.com/metro/2019/09/26/cape-cod-climate-change/story/',
    pubDate: 'September 26, 2019',
    text: 'Climate change on Cape Cod: At the edge of a warming world',
    image: 'https://apps.bostonglobe.com/metro/2019/09/26/cape-cod-climate-change/assets/images/landing_1280.jpg',
  },
  {
    url: 'https://www.pressherald.com/2020/09/07/mayday-gulf-maine-distress-six-part-series-from-colin-woodard/',
    pubDate: 'October 30, 2015',
    text: 'Mayday: Gulf of Maine in distress, a 6-part series on climate change from The Portland Press Herald',
    image: 'https://multifiles.pressherald.com/uploads/sites/4/2015/10/250332-20150906_gulf-of-mai.jpg',
  },
];

// this is UUID in arc Video Center
const videoData = {
  _id: '706d6f29-e8a5-48c8-8ff0-5449aef51d31',
};

const IndexPage = ({
  data: {
    allArcStory: { stories },
    allArcImage: { arcImages },
  },
}) => {
  const heroImage = arcImages[0];
  // crop the image
  heroImage.imgAlign = 'objectPositionRight';
  // make sure first day always goes first
  stories.sort((a, b) => ((a.order > b.order) ? 1 : -1));

  useBusiness({
    environment: process.env.NODE_ENV,
    meterContent: true,
    pageId: 'apps.the-lobster-trap-landing',
    section: 'metro',
    author: '',
    projectName: 'The Lobster Trap',
  });

  return (
    <div className="home">
      <SeoSocial />
      <Topper heroImage={arcImages[0]}>
        <h1 className="miller-n6 series-hed">The lobster trap</h1>
        <p className="bentonsanscond-n4 series-dek">
          The plentiful catch that brought wealth to fishing families is at risk, as climate change warms the Gulf of Maine. A way of life is on the line, but lobstermen can't, or won't, imagine another.
        </p>
      </Topper>

      {/* Stories Coming soon for day 2 at first */}

      <StoryLinks className="content bentonsanscond-n4">
        {/* day 1 */}
        <div className="link" key={stories[0].storyId}>
          <Link to={stories[0].slug}>
            <img
              src={stories[0].promoImage.url}
              srcSet={stories[0].promoImage.srcset}
              sizes="(max-width: 1000px) 50vw, 1000px"
              alt=""
            />
            <div className="pub-date">December 12, 2021</div>
            <h1 className="miller-n6 link-hed">Where lobster is king</h1>
            <p className="bentonsanscond-n4 link-dek">
              The climate disaster that Maine fishermen dread is not some far-off, half-formed threat. It’s here.
            </p>
          </Link>
        </div>
        {/* day 2 */}
        <div className="link" key={stories[1].storyId}>
          <Link to={stories[1].slug}>
            <img
              src={stories[1].promoImage.url}
              srcSet={stories[1].promoImage.srcset}
              sizes="(max-width: 1000px) 50vw, 1000px"
              alt=""
            />
            <div className="pub-date">December 13, 2021</div>
            <h1 className="miller-n6 link-hed">{stories[1].headlines.web || stories[1].headlines.basic}</h1>
            <p className="bentonsanscond-n4 link-dek">{stories[1].subheadlines.basic}</p>
          </Link>
        </div>
      </StoryLinks>
      {/* Information on partnership */}
      <div className="break content"><div className="break-wrap"><Divider /></div></div>

      <SectionHed className="benton-n6">About this partnership</SectionHed>
      <Partnership>
        <div className="logos float-right">
          <GlobeLogo
            viewBox="0 0 500 67"
            width={250}
            height={34}
          />
          <img src={PortlandLogo} alt="Portland Press Herald" />
        </div>
        <p className="paragraph">No news organization is more invested in the health of the Gulf of Maine and the region’s residents than the Portland Press Herald and The Boston Globe. So, as the gulf’s warming waters pose an increasingly existential threat to Maine’s signature industry, lobster, we joined forces to take a hard look at the climate crisis unfolding on our shores.</p>

        <p className="paragraph">We descended on the island of Vinalhaven, one of Maine’s busiest fishing villages, and turned loose reporters, photographers, and videographers from both news organizations to learn firsthand the challenges, stresses, and fears of those facing this uniquely perilous time for lobstering. The problems posed by the warming waters were sometimes subtle &mdash; the slow migration of whales and lobsters, the gradual rise in sea levels. The tensions and stakes were glaring. Climate change gravely threatens the nation’s richest fishery and the communities that depend on it.</p>

        <p className="paragraph">The result is The Lobster Trap, presented here today. It is a story of a group of Mainers struggling to preserve their way of life in a rapidly changing world. It is also a snapshot of life on the front lines of a monumental crisis, no less than last summer’s western wildfires or the rise of extreme flooding events.</p>

        <p className="paragraph">This collaboration was made possible by support from Participant, the company that co-produced the movie &ldquo;Spotlight&rdquo; and funds the Globe’s Spotlight Fellowship program. We are grateful for the continued support and hope that you, our readers, find this work illuminating.</p>

        <p className="paragraph left-margin">&mdash; <em>Boston Globe Editor Brian McGrory and Portland Press Herald Managing Editor Steve Greenlee</em></p>
      </Partnership>
      {/* Video */}

      <div className="videoWrapper">
        <VideoPlayerElement data={videoData} />
      </div>

      <div className="break content"><div className="break-wrap"><Divider /></div></div>

      {/* Gallery */}

      {/* Related */}

      <SectionHed className="benton-n6">Related Coverage</SectionHed>
      <Related>
        {relatedCoverage.map((story) => (
          <a href={story.url} key={story.url}>
            <figure>
              <img
                src={story.image}
                alt=""
              />
            </figure>
            <div>
              <div className="bentonsanscond-n4 pub-date">{story.pubDate}</div>
              <h1 className="miller-n6 related-hed">{story.text}</h1>
            </div>
          </a>
        ))}
      </Related>
      {/* Credit */}
      <Credits />
      {/* <ul>
        <li className="miller-n4">
          Miller and
          <strong className="miller-n6"> Miller Bold</strong>
        </li>
        <li className="benton-n4">
          Benton and
          <strong className="benton-n6"> Benton Bold</strong>
        </li>
        <li className="bentonsanscond-n4">
          Benton Condensed and
          <strong className="bentonsanscond-n6"> Benton Condensed Bold</strong>
        </li>
      </ul> */}

    </div>
  );
};

export const pageQuery = graphql`
    query {
        allArcStory {
            stories: nodes {
                storyId
                order
                headlines {
                    basic
                    web
                }
                subheadlines {
                  basic
                }
                slug
                promoImage {
                  url
                  srcSet
                  credit {
                    name
                  }
                }
            }
        }
        allArcImage {
          arcImages: nodes {
            id
            src
            srcSet
            caption
            credits {
              name
            }
          }
        }
    }
`;

IndexPage.propTypes = {
  data: PropTypes.shape({
    allArcStory: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.shape({
        storyId: PropTypes.string,
        slug: PropTypes.string,
        headlines: PropTypes.shape({
          basic: PropTypes.string,
          web: PropTypes.string,
        }),
      })),
    }),
  }).isRequired,
};

export default IndexPage;
